/**
 * The device key: one non-extractable ECDSA P-256 pair per browser profile,
 * per session (ADR 0040, ADR 0044, docs/plan/14 §6).
 *
 * ## Why non-extractable is the whole design
 *
 * `extractable: false` means the private key exists as a handle script can
 * *use* and never as bytes script can *read*. That is the entire difference
 * between this and a second cookie: a cookie in a log, a backup or a support
 * ticket is a working session, and a key handle in IndexedDB is not, because
 * there is nothing to copy out. XSS on our own origin can still ask the handle
 * to sign — that is ADR 0046's accepted residual and CSP's problem, not this
 * module's.
 *
 * ## One key, many realms
 *
 * IndexedDB is per origin, so every tab, the popup message window (ADR 0020)
 * and the service worker see the same record. First-boot races are serialised
 * with `navigator.locks` where it exists; where it does not, the storage layer
 * resolves the race by keeping whichever record was written first and handing
 * the loser the winner's key, so two tabs can never end up signing with two
 * different keys.
 *
 * ## Lifetime
 *
 * Key lifetime *is* session lifetime (ADR 0044). Logout, a session-ending 401
 * and revocation delete it; the next login generates a fresh one. A thumbprint
 * therefore never links two owners of a shared machine, and "the record names a
 * different `kid`" can only mean another tab re-enrolled.
 *
 * ## When there is no key
 *
 * `absent` (storage worked, nothing stored) and `unsupported` (no WebCrypto, no
 * IndexedDB, or storage access threw — a private window, a locked-down
 * profile) are **different answers** and the UI treats them differently
 * (ADR 0042). Neither is ever resolved by silently continuing as if bound.
 */

import {
  jwkThumbprint,
  PROOF_KEY_ALGORITHM,
  toDeviceKeyJwk,
  type DeviceKeyJwk,
  type ProofSigner,
} from './proof';

/** IndexedDB database, store and record id. One record, ever. */
export const DEVICE_KEY_DB = 'ols-mail-keys';
export const DEVICE_KEY_STORE = 'device';
export const DEVICE_KEY_ID = 'current';
/** `navigator.locks` name that serialises first-boot generation across tabs. */
export const DEVICE_KEY_LOCK = 'ols-device-key';

/** The stored record. `privateKey` is a handle; its bytes are unreachable. */
export interface StoredDeviceKey {
  readonly id: typeof DEVICE_KEY_ID;
  readonly privateKey: CryptoKey;
  readonly publicKey: CryptoKey;
  readonly kid: string;
  readonly jwk: DeviceKeyJwk;
  readonly createdAt: string;
}

/** The usable key, as the transport and the enrolment bodies want it. */
export interface DeviceKey extends ProofSigner {
  readonly kid: string;
  readonly jwk: DeviceKeyJwk;
  readonly privateKey: CryptoKey;
  readonly createdAt: string;
}

/** Why this browser cannot hold a key at all. */
export type DeviceKeyUnsupportedReason =
  /** No `crypto.subtle`: an insecure context, or a browser without WebCrypto. */
  | 'no-webcrypto'
  /** No IndexedDB at all. */
  | 'no-storage'
  /** IndexedDB exists but refused us — private window, blocked site data. */
  | 'storage-blocked'
  /** WebCrypto is present but could not produce a P-256 key. */
  | 'keygen-failed';

export type DeviceKeyState =
  | { readonly status: 'ready'; readonly key: DeviceKey }
  /** Storage works; there is simply no key yet (or it was deleted). */
  | { readonly status: 'absent' }
  | { readonly status: 'unsupported'; readonly reason: DeviceKeyUnsupportedReason };

/* --------------------------------------------------------------- storage */

/**
 * The persistence seam.
 *
 * An interface rather than direct IndexedDB calls because jsdom has no
 * IndexedDB at all: the tests drive a real in-memory implementation of exactly
 * this contract, so everything above it — generation, caching, races, deletion,
 * the degradation branches — is exercised for real, and only the ~40 lines of
 * `idb` plumbing below are left to a browser to prove.
 */
export interface DeviceKeyStorage {
  read(): Promise<StoredDeviceKey | undefined>;
  /**
   * Writes `record` only if nothing is stored, and returns whatever is stored
   * afterwards — the winner of a race, which may not be `record`.
   */
  writeIfAbsent(record: StoredDeviceKey): Promise<StoredDeviceKey>;
  clear(): Promise<void>;
}

/** In-memory storage: the test double, and nothing else uses it. */
export function createMemoryDeviceKeyStorage(): DeviceKeyStorage {
  let stored: StoredDeviceKey | undefined;
  return {
    read: () => Promise.resolve(stored),
    writeIfAbsent: (record) => {
      stored ??= record;
      return Promise.resolve(stored);
    },
    clear: () => {
      stored = undefined;
      return Promise.resolve();
    },
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DEVICE_KEY_DB, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(DEVICE_KEY_STORE)) {
        request.result.createObjectStore(DEVICE_KEY_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

/** The real thing. Kept deliberately small; see {@link DeviceKeyStorage}. */
export function createIndexedDbDeviceKeyStorage(): DeviceKeyStorage {
  const withStore = async <T>(
    mode: IDBTransactionMode,
    body: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T> => {
    const db = await openDatabase();
    try {
      return await body(db.transaction(DEVICE_KEY_STORE, mode).objectStore(DEVICE_KEY_STORE));
    } finally {
      db.close();
    }
  };

  return {
    read: () =>
      withStore('readonly', async (store) => {
        const record = await promisify<unknown>(store.get(DEVICE_KEY_ID));
        return isStoredKey(record) ? record : undefined;
      }),
    writeIfAbsent: (record) =>
      withStore('readwrite', async (store) => {
        try {
          // `add`, not `put`: a concurrent realm that got there first wins the
          // constraint error, and we adopt its key rather than overwriting a
          // key another tab may already have enrolled.
          await promisify(store.add(record));
          return record;
        } catch {
          const existing = await promisify<unknown>(store.get(DEVICE_KEY_ID));
          return isStoredKey(existing) ? existing : record;
        }
      }),
    clear: () => withStore('readwrite', (store) => promisify(store.delete(DEVICE_KEY_ID))),
  };
}

function isStoredKey(value: unknown): value is StoredDeviceKey {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Partial<StoredDeviceKey>;
  return (
    record.id === DEVICE_KEY_ID &&
    typeof record.kid === 'string' &&
    record.privateKey !== undefined &&
    record.jwk !== undefined
  );
}

/* ----------------------------------------------------------- module state */

let storageFactory: () => DeviceKeyStorage = createIndexedDbDeviceKeyStorage;
let storage: DeviceKeyStorage | undefined;
/** In-memory handle cache: one IndexedDB round trip per key, not per request. */
let cached: DeviceKeyState | undefined;
/** De-duplicates concurrent callers within this realm. */
let inFlight: Promise<DeviceKeyState> | undefined;

/**
 * Test seam. Swaps the persistence layer and clears every cache, so a case
 * starts from a cold profile.
 */
export function setDeviceKeyStorage(factory: (() => DeviceKeyStorage) | undefined): void {
  storageFactory = factory ?? createIndexedDbDeviceKeyStorage;
  storage = undefined;
  cached = undefined;
  inFlight = undefined;
}

function storageOrThrow(): DeviceKeyStorage {
  storage ??= storageFactory();
  return storage;
}

/** True when this realm could hold a key at all. Cheap, synchronous. */
export function deviceKeySupported(): boolean {
  if (globalThis.crypto?.subtle === undefined) return false;
  // The IndexedDB check is skipped when a storage factory was injected: a test
  // (or the service worker's own adapter) supplies persistence directly.
  if (storageFactory !== createIndexedDbDeviceKeyStorage) return true;
  return typeof indexedDB !== 'undefined';
}

function unsupported(reason: DeviceKeyUnsupportedReason): DeviceKeyState {
  cached = { status: 'unsupported', reason };
  return cached;
}

/**
 * The key this realm should sign with, without generating one.
 *
 * Returns the cached handle when there is one; otherwise reads storage once.
 * `absent` here is the lost-key case that ADR 0042 answers with a password
 * dialog, and it is *not* an error.
 */
export async function loadDeviceKey(): Promise<DeviceKeyState> {
  if (cached !== undefined) return cached;
  if (globalThis.crypto?.subtle === undefined) return unsupported('no-webcrypto');
  if (!deviceKeySupported()) return unsupported('no-storage');
  try {
    const record = await storageOrThrow().read();
    cached = record === undefined ? { status: 'absent' } : { status: 'ready', key: toKey(record) };
    return cached;
  } catch {
    // Reading threw: a private window with storage denied, or a corrupt
    // database. Either way this browser cannot hold a key right now, and
    // saying so is the ADR 0042 requirement.
    return unsupported('storage-blocked');
  }
}

/**
 * The key, generating one if the profile has none.
 *
 * Called on the login page before submit, and by the recovery flow. Generation
 * is milliseconds, so it never sits on the submit path; `persist()` is
 * requested at the same moment, because a key evicted from a 30-day remembered
 * session is the common failure this whole module is shaped around.
 */
export async function ensureDeviceKey(): Promise<DeviceKeyState> {
  const existing = await loadDeviceKey();
  if (existing.status !== 'absent') return existing;
  inFlight ??= generateUnderLock().finally(() => {
    inFlight = undefined;
  });
  return inFlight;
}

async function generateUnderLock(): Promise<DeviceKeyState> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (locks?.request === undefined) return generateAndStore();
  // `navigator.locks` serialises the three-tabs-from-cold case. Where it is
  // missing (Safari < 15.4, workers in older builds) `writeIfAbsent` still
  // makes the race safe — the lock only saves a wasted keygen.
  return (await locks.request(DEVICE_KEY_LOCK, () => generateAndStore())) as DeviceKeyState;
}

async function generateAndStore(): Promise<DeviceKeyState> {
  // Another realm may have written one while we waited for the lock.
  cached = undefined;
  const raced = await loadDeviceKey();
  if (raced.status !== 'absent') return raced;

  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) return unsupported('no-webcrypto');

  let record: StoredDeviceKey;
  try {
    const pair = (await subtle.generateKey(PROOF_KEY_ALGORITHM, false, ['sign', 'verify'])) as
      | CryptoKeyPair
      | undefined;
    if (pair?.privateKey === undefined || pair.publicKey === undefined) {
      return unsupported('keygen-failed');
    }
    if (pair.privateKey.extractable) {
      // Never observed in a conforming implementation; refused rather than
      // trusted, because an extractable "non-extractable" key is precisely the
      // property this design rests on.
      return unsupported('keygen-failed');
    }
    const jwk = toDeviceKeyJwk(await subtle.exportKey('jwk', pair.publicKey));
    if (jwk === undefined) return unsupported('keygen-failed');
    record = {
      id: DEVICE_KEY_ID,
      privateKey: pair.privateKey,
      publicKey: pair.publicKey,
      jwk,
      kid: await jwkThumbprint(jwk),
      createdAt: new Date().toISOString(),
    };
  } catch {
    return unsupported('keygen-failed');
  }

  void requestPersistence();

  try {
    const stored = await storageOrThrow().writeIfAbsent(record);
    cached = { status: 'ready', key: toKey(stored) };
    return cached;
  } catch {
    return unsupported('storage-blocked');
  }
}

/**
 * Asks the browser to exempt our origin from eviction heuristics.
 *
 * Best effort by definition: Chrome grants it on engagement, Firefox prompts,
 * Safari ignores it. A refusal is not an error — it is the reason the recovery
 * dialog exists.
 */
async function requestPersistence(): Promise<void> {
  try {
    await (globalThis.navigator as Navigator | undefined)?.storage?.persist?.();
  } catch {
    /* Not available, or refused. Nothing to do about it. */
  }
}

function toKey(record: StoredDeviceKey): DeviceKey {
  return {
    kid: record.kid,
    jwk: record.jwk,
    privateKey: record.privateKey,
    createdAt: record.createdAt,
  };
}

/**
 * Drops the in-memory handle so the next request re-reads storage.
 *
 * Called on any `login` / `logout` / `reauthenticated` broadcast (doc 14 §4)
 * and after a `401 proof_invalid`, which is how a tab notices that a sibling
 * rebound the session under it.
 */
export function forgetCachedDeviceKey(): void {
  cached = undefined;
  inFlight = undefined;
}

/** The cached handle without touching storage. `undefined` when not loaded. */
export function cachedDeviceKey(): DeviceKey | undefined {
  return cached?.status === 'ready' ? cached.key : undefined;
}

/**
 * Deletes the key. Part of session teardown — the key must not outlive the
 * session that enrolled it, or a thumbprint would link two owners of one
 * machine (ADR 0044).
 */
export async function deleteDeviceKey(): Promise<void> {
  forgetCachedDeviceKey();
  try {
    await storageOrThrow().clear();
  } catch {
    /* Nothing stored, or storage is gone. Either way there is no key now. */
  }
}
